import React, { useEffect, useState } from 'react';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { Section } from 'components/section';
import { Field } from 'components/field';
import { NumberField } from 'components/form-fields-2';
import {
  detailsFormationName,
  InventoryTypeCurveDetailsResponse,
} from 'types/api-responses';
import { format0 } from 'utils/number';
import style from './inventory-details-section.module.scss';
// The Type Curve Details edit form's grid — reused so both sections lay out
// their fields identically (6 even columns, shared input sizing).
import formStyle from './type-curve-details-section.module.scss';

/** The four editable spacing parameters, as sent with Compute Inventory. */
export interface InventorySpacingValues {
  wellSpacingFt: number | null;
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
  spacingToleranceFt: number | null;
}

interface InventoryDetailsSectionProps {
  data: InventoryTypeCurveDetailsResponse;
  /** Unpromoted workspaces edit the spacing values; promoted ones read them. */
  editable?: boolean;
  /** A compute run is in flight — keeps the button disabled meanwhile. */
  computing?: boolean;
  /**
   * A compute error (e.g. the formation can't be computed) blocks the button —
   * even after a spacing edit — until the operator changes formation.
   */
  computeBlocked?: boolean;
  /**
   * Resolve `false` when the request fails: the baseline is restored so the
   * edits stay dirty and the button is clickable again for a retry.
   */
  onCompute?: (spacing: InventorySpacingValues) => void | Promise<boolean>;
}

const spacingOf = (
  data: InventoryTypeCurveDetailsResponse
): InventorySpacingValues => ({
  wellSpacingFt: data.wellSpacingFt,
  crossLineSetbackFt: data.crossLineSetbackFt,
  inLineSetbackFt: data.inLineSetbackFt,
  spacingToleranceFt: data.spacingToleranceFt,
});

const sameSpacing = (a: InventorySpacingValues, b: InventorySpacingValues) =>
  a.wellSpacingFt === b.wellSpacingFt &&
  a.crossLineSetbackFt === b.crossLineSetbackFt &&
  a.inLineSetbackFt === b.inLineSetbackFt &&
  a.spacingToleranceFt === b.spacingToleranceFt;

const feet = (value: number | null) => {
  return value !== null ? `${format0(value)} ft` : '--';
};

/**
 * Every status the panel shows, in the order the operator reads them, keyed to
 * the zone payload's `propertyCountsByStatus`. All five — the four drilled
 * statuses and LTD — come from that single source (the resolved slot inventory),
 * so the panel agrees with the zone endpoint by construction. Labels are this
 * section's own.
 */
const STATUS_FIELDS: {
  key: keyof InventoryTypeCurveDetailsResponse['propertyCountsByStatus'];
  label: string;
}[] = [
  { key: 'producing', label: 'Producing' },
  { key: 'duc', label: 'DUC' },
  { key: 'permit', label: 'Permit' },
  { key: 'shutIn', label: 'Shut-in' },
  { key: 'ltd', label: 'LTD' },
];

const SPACING_FIELDS: {
  key: keyof InventorySpacingValues;
  label: string;
}[] = [
  { key: 'wellSpacingFt', label: 'Well Spacing (ft)' },
  { key: 'crossLineSetbackFt', label: 'Cross-line Setback (ft)' },
  { key: 'inLineSetbackFt', label: 'In-line Setback (ft)' },
  { key: 'spacingToleranceFt', label: 'Spacing Tolerance (ft)' },
];

/**
 * Spacing/setback parameters and per-status property counts for the selected
 * zone, from GET /inventory_workspaces/:id/inventory_formation_packages/:id.
 * Per the Inventory Workspace mock: unpromoted workspaces edit the spacing
 * values and trigger Compute Inventory (enabled for the first run, then only
 * while dirty once already computed); promoted ones show the values read-only.
 * @param {InventoryDetailsSectionProps} props - The inventory details payload and edit wiring.
 * @return {JSX.Element} The section.
 */
export const InventoryDetailsSection: React.FC<
  InventoryDetailsSectionProps
> = ({
  data,
  editable = false,
  computing = false,
  computeBlocked = false,
  onCompute,
}) => {
  // Every status count comes from the zone payload's `propertyCountsByStatus`
  // (the resolved slot inventory), so the panel agrees with the zone endpoint
  // by construction. It no longer reads the live wells query — that query is
  // filtered and unit-bounded, so it counted a narrower set of wells than the
  // inventory recorded and drifted from the API's own numbers.
  const counts = data.propertyCountsByStatus;
  // The drilled statuses sum to the well total; LTD is an undrilled location, so
  // the property total is the well total plus LTD.
  const wellsTotal =
    (counts.producing ?? 0) +
    (counts.duc ?? 0) +
    (counts.permit ?? 0) +
    (counts.shutIn ?? 0);
  const totalProperties = wellsTotal + (counts.ltd ?? 0);
  // From the zone payload (inventory_formation_packages/:zoneId). Currently the
  // backend hardcodes this false; the gating is ready for when it goes live.
  const hasComputedInventory = data.hasComputedInventory ?? false;
  const [values, setValues] = useState<InventorySpacingValues>(() =>
    spacingOf(data)
  );
  // The last computed (or server-loaded) values — the dirty baseline. Edits
  // stay client-side until Compute sends them with the generation request.
  const [applied, setApplied] = useState<InventorySpacingValues>(() =>
    spacingOf(data)
  );

  // Reseed when the selected leaf changes; edits on the previous leaf are
  // intentionally discarded (they only ever travel via Compute Inventory).
  useEffect(() => {
    setValues(spacingOf(data));
    setApplied(spacingOf(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id]);

  const dirty = !sameSpacing(values, applied);
  // Why the backend says this zone cannot compute, from the load payload — no
  // promoted type curve, no matching area config, or a formation no structure
  // grid and no area well can give a slot depth. Null when it can compute.
  // Older payloads carry neither key and read as computable.
  const unavailableReason =
    data.canComputeInventory === false
      ? (data.computeBlockedReason ??
        'Inventory cannot be computed for this formation.')
      : null;

  const handleCompute = async () => {
    // Mock behavior: the button re-disables immediately; the run itself is
    // async (Pusher-tracked on the real backend). A rejected request restores
    // the baseline, so the edits are still dirty and the retry is one click.
    const previous = applied;
    setApplied(values);
    const started = await onCompute?.(values);
    if (started === false) setApplied(previous);
  };

  const computeButton = (
    <Button
      kind="primary"
      size="sm"
      // Enabled to run the first time (never computed) or once the operator
      // edits a spacing value to recompute; disabled while a run is in flight,
      // when already computed and untouched, and unconditionally when the zone
      // cannot compute at all — no spacing value changes that answer, so an
      // edit must not arm a run the POST would refuse.
      disabled={
        computing ||
        computeBlocked ||
        unavailableReason !== null ||
        (hasComputedInventory && !dirty)
      }
      onClick={handleCompute}
    >
      {/* Spinner while a run is in flight (the button is always disabled
          meanwhile); otherwise the label. */}
      {computing ? (
        <InlineLoading description="Computing..." />
      ) : (
        'Compute Inventory'
      )}
    </Button>
  );

  return (
    <div className={style.detailsSection}>
      <Section
        header={
          <>
            Inventory Details
            <span className={style.packageName}>
              | {data.inventoryPackageName ?? 'New Package'}
              {data.isDefaultPackage ? ' (Default)' : ''}
            </span>
          </>
        }
        headerActions={
          editable ? (
            <div className={style.computeAction}>{computeButton}</div>
          ) : undefined
        }
      >
        {/* The reason a disabled Compute Inventory will stay disabled, stated in
            the section rather than on a hover: the button emits no hover events
            while disabled, and the operator needs to know why before they start
            editing spacing values that cannot change the answer. Only where the
            button exists — a promoted workspace never computes, so the warning
            would be noise there. */}
        {editable && unavailableReason !== null && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={style.computeBlockedNotice}
            title="Inventory cannot be computed for this zone"
            subtitle={unavailableReason}
          />
        )}
        {/* Same 6-column grid as the Type Curve Details edit form, so labels
            stay on one line and inputs share a baseline (mock parity). */}
        <div className={formStyle.formGrid}>
          {SPACING_FIELDS.map(({ key, label }) => (
            <div key={key} className={formStyle.formField}>
              {editable ? (
                <NumberField
                  id={`inventory-details-${key}`}
                  label={label}
                  size="sm"
                  value={values[key]}
                  onChange={(n) => setValues((prev) => ({ ...prev, [key]: n }))}
                />
              ) : (
                <Field label={label} value={feet(data[key])} />
              )}
            </div>
          ))}
          <div className={formStyle.formField}>
            <Field
              label="Type Curve Area"
              value={`${data.typeCurveArea}${data.isOrigin ? ' · Origin' : ''}`}
            />
          </div>
          <div className={formStyle.formField}>
            <Field
              label="Formation"
              value={detailsFormationName(data) || '--'}
            />
          </div>
        </div>

        <p className={style.statusCountsHeading}>
          Property Counts by Status
          {/* Both totals, because the panel has to answer two questions at
              once: the well figure is the drilled statuses added up; the
              property figure is every field below added up, LTD included. */}
          <span className={style.statusCountsTotal}>
            {format0(wellsTotal)} wells · {format0(totalProperties)} properties
          </span>
        </p>
        <div className={formStyle.formGrid}>
          {STATUS_FIELDS.map(({ key, label }) => (
            <div key={key} className={formStyle.formField}>
              <Field label={label} value={format0(counts[key])} />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};
